import type { SheetSide } from "./types";

/**
 * Side → position + slide-animation classes for the internal mobile Sheet.
 *
 * Side positioning and the slide transition are **pure Tailwind on the dialog
 * Content we already expose** — no new prop on the dialog primitive and no
 * second wrapper around `@radix-ui/react-dialog`. Each entry pins the panel to
 * an edge, sets the full-height mobile width, and drives the enter/exit slide
 * off Radix's `data-state` (via the `tailwindcss-animate` keyframes).
 *
 * Spelled out as literals so Tailwind JIT picks them up at compile time.
 *
 * `--sidebar-width-mobile` is set by the vendored shadcn block on web; the
 * `18rem` fallback keeps the panel sized when the Sheet renders standalone
 * (e.g. in isolation before the shell mounts).
 */
const sheetBase = "fixed inset-y-0 z-50 flex h-full w-[var(--sidebar-width-mobile,18rem)] flex-col";

export const sheetSideClasses: Record<SheetSide, string> = {
  left:
    sheetBase +
    " left-0 border-r " +
    "data-[state=open]:animate-in data-[state=open]:slide-in-from-left " +
    "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left",
  right:
    sheetBase +
    " right-0 border-l " +
    "data-[state=open]:animate-in data-[state=open]:slide-in-from-right " +
    "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
};

/** Surface chrome shared by both sides — background + section border token. */
export const sheetSurfaceClasses =
  "bg-luce-neutral-background-default border-luce-neutral-border-mediumEmphasis outline-none";

/** Enter/exit fade duration applied alongside the slide. */
export const sheetAnimationClasses = "duration-200";

/**
 * Scrim behind the panel. Matches the Dialog molecule's default overlay so the
 * mobile sidebar dims the page consistently with other modals.
 */
export const sheetOverlayClasses =
  "fixed inset-0 z-40 bg-luce-overlay-20 " +
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 " +
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0";
